import { ChatBotUIDateSlot } from "@jugl-web/domain-resources/chat-bot/types";
import { cx } from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import utcToZonedTime from "date-fns-tz/utcToZonedTime";
import { CSSProperties, FC, useCallback, useMemo, useState } from "react";
import { QuickReplyButton } from "../QuickRepliesList";
import { BookingAttemptedMessage } from "./BookingAttemptedMessage";
import { DaySlotPicker } from "./DaySlotPicker";
import { TimeSlotPicker } from "./TimeSlotPicker";

interface ChatBotDateSlotBookingProps {
  businessTimezoneCode: string;
  slots: ChatBotUIDateSlot[];
  className?: string;
  style?: CSSProperties;
}

const SLOT_BOOKING_TIMEZONE_TYPE_STORAGE_KEY = "jugl:slotBookingTimezoneType";

export const ChatBotDateSlotBooking: FC<ChatBotDateSlotBookingProps> = ({
  businessTimezoneCode,
  slots,
  className,
  style,
}) => {
  const [timezoneType, setTimezoneType] = useLocalStorage<
    "business" | "client"
  >(SLOT_BOOKING_TIMEZONE_TYPE_STORAGE_KEY, "client");

  const clientTimezoneCode = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );

  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  const selectedSlot = useMemo<ChatBotUIDateSlot | null>(() => {
    if (!selectedSlotId) {
      return null;
    }

    return slots.find((slot) => slot.id === selectedSlotId) ?? null;
  }, [selectedSlotId, slots]);

  const zoneSlot = useCallback(
    (slot: ChatBotUIDateSlot): ChatBotUIDateSlot => ({
      ...slot,
      startTime: utcToZonedTime(slot.startTime, businessTimezoneCode),
      endTime: utcToZonedTime(slot.endTime, businessTimezoneCode),
    }),
    [businessTimezoneCode]
  );

  const timezoneAwareSlots = useMemo<ChatBotUIDateSlot[]>(
    () => (timezoneType === "client" ? slots : slots.map(zoneSlot)),
    [slots, timezoneType, zoneSlot]
  );

  const handleSlotSelect = (slotId: string) => {
    let parsedUrl: URL;

    try {
      parsedUrl = new URL(slotId);
    } catch {
      return;
    }

    if (parsedUrl.protocol !== "https:" && parsedUrl.protocol !== "http:") {
      return;
    }

    setSelectedSlotId(slotId);
    window.open(parsedUrl.toString(), "_blank", "noopener,noreferrer");
  };

  const reset = () => {
    setSelectedDay(null);
    setSelectedSlotId(null);
  };

  const content = useMemo(() => {
    if (selectedSlot) {
      return (
        <BookingAttemptedMessage
          selectedSlot={
            timezoneType === "client" ? selectedSlot : zoneSlot(selectedSlot)
          }
        />
      );
    }

    if (selectedDay) {
      return (
        <TimeSlotPicker
          slots={timezoneAwareSlots}
          selectedDay={selectedDay}
          clientTimezoneCode={clientTimezoneCode}
          businessTimezoneCode={businessTimezoneCode}
          selectedTimezoneType={timezoneType}
          onSelect={handleSlotSelect}
          onGoBack={() => setSelectedDay(null)}
          onTimezoneTypeChange={setTimezoneType}
        />
      );
    }

    return (
      <DaySlotPicker
        slots={timezoneAwareSlots}
        clientTimezoneCode={clientTimezoneCode}
        businessTimezoneCode={businessTimezoneCode}
        selectedTimezoneType={timezoneType}
        onSelect={setSelectedDay}
        onTimezoneTypeChange={setTimezoneType}
      />
    );
  }, [
    businessTimezoneCode,
    clientTimezoneCode,
    selectedDay,
    selectedSlot,
    setTimezoneType,
    timezoneAwareSlots,
    timezoneType,
    zoneSlot,
  ]);

  return (
    <div className={cx("w-full", className)} style={style}>
      <div className="rounded-2xl border border-solid border-dark-200 bg-white p-6">
        {content}
      </div>
      {selectedSlot && (
        <QuickReplyButton
          className="text mt-2 rounded-[20px] bg-grey-100"
          onClick={reset}
        >
          Book another slot
        </QuickReplyButton>
      )}
    </div>
  );
};
